import React, { ReactElement, useState, useEffect, useRef, FocusEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import TextArea from '@Common/TextArea';
import InputText from '@Common/InputText';
import FileChooser from '@Common/FileChooser';
import { FILE_STATUSES } from '@Constants/constants';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import {
    useCreateLink,
    useUpdateLink,
    useUploadFile,
    useUpdateFile,
    useDeleteFile,
    invalidateTask,
} from '@Apis/locationTasks';
import { useDownloadFile } from '@Apis/files';
import { Modal61Ref } from '@Types/common';
import { getUrlNoScheme, getUrlScheme } from '@Helpers/routing';
import { isValidUrl } from '@Helpers/validation';
import { LocationTaskLink, LocationTaskFile } from './Types';
import FileCell from './FileCell';
import ImageGalleryModal from './ImageGalleryModal';
import './LinksAndFiles.scss';

interface LinksAndFilesProps {
    locationTaskId: number;
    links: Array<LocationTaskLink> | undefined;
    files: Array<LocationTaskFile> | undefined;
    isMobile: boolean;
}

const LinksAndFiles = (props: LinksAndFilesProps): ReactElement => {
    const [links, setLinks] = useState<Array<LocationTaskLink>>(props.links ?? []); // Get the initial links...
    const [files, setFiles] = useState<Array<LocationTaskFile>>(props.files ?? []); // ... and files from props but we manage them hereafter
    const [isFileFormValid, setIsFileFormValid] = useState<boolean>(true);
    const [isSavingLink, setIsSavingLink] = useState<boolean>(false);
    const [isSavingFile, setIsSavingFile] = useState<boolean>(false);
    const [activeImageIndex, setActiveImageIndex] = useState<any>({ index: -1, opened: 0 });
    const [editLinkIds, setEditLinkIds] = useState<Array<number>>([]);  // The ids of existing links...
    const [editFileIds, setEditFileIds] = useState<Array<number>>([]);  // ... and files that are currently in edit mode

    // Sync from props when data loads asynchronously after initial mount (avoids stale empty state
    // when the drawer opens before the API response has arrived)
    useEffect(() => {
        const hasActiveEdits = editLinkIds.length > 0 || links.some(l => l.id === 0);
        if (!hasActiveEdits) {
            setLinks(props.links ?? []);
        }
    }, [props.links]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const hasActiveEdits = editFileIds.length > 0 || files.some(f => f.id === 0);
        if (!hasActiveEdits) {
            setFiles(props.files ?? []);
        }
    }, [props.files]); // eslint-disable-line react-hooks/exhaustive-deps

    const queryClient = useQueryClient();

    const downloadFileMutation = useDownloadFile();
    const createLinkMutation = useCreateLink((result) => {
        if (result) invalidateTask(queryClient, props.locationTaskId);
    });
    const updateLinkMutation = useUpdateLink((result) => {
        if (result) invalidateTask(queryClient, props.locationTaskId);
    });
    const uploadFileMutation = useUploadFile((result) => {
        if (result) invalidateTask(queryClient, props.locationTaskId);
    });
    const updateFileMutation = useUpdateFile((result) => {
        if (result) invalidateTask(queryClient, props.locationTaskId);
    });
    const deleteFileMutation = useDeleteFile((result) => {
        if (result) invalidateTask(queryClient, props.locationTaskId);
    });

    const galleryModalRef = useRef<Modal61Ref>(null);

    const linksAndFilesCount = (links.length) + (files.length);

    const getMutationMessage = (error: unknown, fallback: string): string =>
        error instanceof Error ? error.message : fallback;

    const getSavedFile = (result: unknown): LocationTaskFile | undefined =>
        (result as { file?: LocationTaskFile })?.file;

    const getSavedLink = (result: unknown): LocationTaskLink | undefined =>
        (result as { link?: LocationTaskLink })?.link;

    const onAddFile = () => {
        const newFiles = files.slice();

        // Add a new file placeholder at the start of the list so it is visible immediately
        newFiles.unshift({
            id: 0,
            locationTaskId: props.locationTaskId,
            name: "",
            editingName: "",
            description: "",
            editingDescription: "",
            extension: "",
            fileStatusId: FILE_STATUSES.Pending,
            fileName: "",
            storageName: "",
            imageObjectURL: "",
            isActive: true,
            file: null,
            showFileInput: false,
        });

        setFiles(newFiles);
    }

    const onEditFile = (id: number) => {
        const newFiles = files.slice();
        const index = newFiles.findIndex(x => x.id === id);
        const file = newFiles[index];
        file.editingName = file.name;
        file.editingDescription = file.description ?? '';
        newFiles.splice(index, 1, file);
        const updatedList = [...editFileIds, file.id];
        setEditFileIds(updatedList);
    };

    const updateFile = (file, fieldName, fieldValue) => {
        const newFiles = files.slice();
        const index = newFiles.findIndex(x => x.id === file.id);
        file[fieldName] = fieldValue;
        newFiles.splice(index, 1, file);
        setFiles(newFiles);
    }

    const onCancelFile = (file: LocationTaskFile) => {
        const newFiles = files.slice();

        if (file.id === 0) {
            // Remove the first file from the list, which is the one that was being added
            newFiles.shift();
        } else if (isFileInEdit(file.id)) {
            // Clear the editing values
            file.editingName = "";
            file.editingDescription = "";
            file.showFileInput = false;
            const index = newFiles.findIndex(x => x.id === file.id);
            newFiles.splice(index, 1, file);

            // Clear the file from the edit list
            const updatedEditIds = editFileIds.filter(x => x !== file.id);
            setEditFileIds(updatedEditIds);
        }

        setIsFileFormValid(true);
        setFiles(newFiles);
    }

    const onDeleteFile = async (file: LocationTaskFile) => {
        setIsSavingFile(true);
        const request = {
            ...file,
            isActive: false,
            locationTaskId: props.locationTaskId,
        };

        deleteFileMutation.mutate(request, {
            onSuccess: () => {
                const newFiles = files.slice();
                const index = newFiles.findIndex(x => x.id === file.id);
                newFiles.splice(index, 1);
                setFiles(newFiles);
                Notification("success", "File Deleted", "");
            },
            onError: (error) => Notification("error", "Error", getMutationMessage(error, 'Unable to delete file')),
            onSettled: () => setIsSavingFile(false),
        });
    }

    const onFileReplaced = (file: LocationTaskFile, e: React.ChangeEvent<HTMLInputElement>) => {
        const replacedFile = e.target.files?.[0];
        if (replacedFile) {
            updateFile(file, "file", replacedFile);
        }
    }

    const validateFile = (file: LocationTaskFile) => {
        const isNewFile = file.id === 0;
        const hasFileBytes = file.file && file.file.name && file.file.name.trim().length > 0;
        const hasRequiredName = file.editingName && file.editingName.trim().length > 0;
        const isValidNewFile = isNewFile && hasFileBytes && hasRequiredName;

        const isExistingFile = !isNewFile;
        // Is replacing file and file has been chosen, or is not replacing file
        const hasRequiredFileReplacement = (file.showFileInput && hasFileBytes) || !file.showFileInput;
        const isValidExistingFile = isExistingFile && hasRequiredName && hasRequiredFileReplacement;

        return isValidNewFile || isValidExistingFile;
    };

    const onSaveFile = async (file: LocationTaskFile) => {
        let result = false;
        if (validateFile(file)) {
            setIsFileFormValid(true);
            setIsSavingFile(true);

            const request = {
                ...file,
                name: file.editingName,
                description: file.editingDescription,
                //fileStatusId: file.fileStatusId,
                locationTaskId: props.locationTaskId,
            };

            return await new Promise<boolean>((resolve) => {
                const mutation = file.id === 0 ? uploadFileMutation : updateFileMutation;
                mutation.mutate(request, {
                    onSuccess: (response) => {
                        const savedFile = getSavedFile(response);
                        if (savedFile) {
                            handleSavedFile(savedFile, file.id === 0);
                        }
                        Notification('success', 'Task Updated', 'File has been saved successfully');
                        resolve(true);
                    },
                    onError: (error) => {
                        Notification('error', 'Error', getMutationMessage(error, 'Unable to save file'));
                        resolve(false);
                    },
                    onSettled: () => setIsSavingFile(false),
                });
            });
        } else {
            setIsFileFormValid(false);
        }

        return result;
    };

    const handleSavedFile = (file: LocationTaskFile, isNew: boolean = false) => {
        if (isNew) {
            // For new files, add the saved file to the list
            setFiles(prev => [file, ...prev.filter(f => f.id !== 0)]); // Remove the temporary file with id 0
            return;
        }

        setFiles(prev => {
            const newFiles = prev.slice();
            const index = newFiles.findIndex(x => x.id === file.id);

            if (index >= 0) {
                // Replace existing file
                newFiles.splice(index, 1, file);
            } else {
                // Insert new file (shouldn't happen in normal flow, but safety net)
                newFiles.push(file);
            }

            return newFiles;
        });

        // Clear the file from the edit list
        setEditFileIds(prev => prev.filter(x => x !== file.id));
    };

    // this is for private files that were converted to links (link.baselineTaskLinkId != null)
    const onDownloadLink = async (link, e) => {
        e.preventDefault();
        try {
            // extract the file name from the link.url
            const start = link.url.indexOf("=") + 1;
            const fileName = link.url.substr(start, link.url.length - start);

            downloadFileMutation.mutate(fileName, {
                onError: (error) => Notification("error", "Error", getMutationMessage(error, 'Unable to download file')),
            });
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    const showGallery = (fileName: string) => {
        const idx = files.filter(f => f.imageObjectURL).findIndex(f => f.name === fileName);
        setActiveImageIndex({ index: idx < 0 ? 0 : idx, opened: activeImageIndex.opened + 1 });
    }

    useEffect(() => {
        // Show the gallery modal when React gets around to setting the active image index
        if (activeImageIndex.index >= 0) {
            galleryModalRef.current?.show();
        }
    }, [activeImageIndex]);

    const isLinkInEdit = (id: number) => {
        return id === 0 || editLinkIds.some(x => x === id);
    };

    const isFileInEdit = (id: number) => {
        return id === 0 || editFileIds.some(x => x === id);
    };

    const onAddLink = () => {

        const nextOrdinal = links.reduce((max, current) => Math.max(max, current.ordinal), 0) + 1;
        const newLinks = links.slice();
        newLinks.push({
            id: 0,
            locationTaskId: props.locationTaskId,
            baselineTaskLinkId: null,
            customerFileId: null,
            ordinal: nextOrdinal,
            name: "",
            editingName: "",
            url: "",
            editingUrl: "",
            isActive: true
        });

        setLinks(newLinks);
    };

    const onEditLink = (index: number) => {
        const newLinks = links.slice();
        const link = newLinks[index];
        link.editingUrl = link.url;
        link.editingName = link.name;
        newLinks.splice(index, 1, link);

        const updatedList = [...editLinkIds, link.id];
        setEditLinkIds(updatedList);
    };

    const onRemoveLink = async (index: number) => {
        const link = links[index];
        link.isActive = false;
        setIsSavingLink(true);
        updateLinkMutation.mutate(link, {
            onSuccess: () => {
                Notification("success", "Link Deleted Successfully", "");
                const newLinks = links.slice();
                newLinks.splice(index, 1);
                setLinks(newLinks);
            },
            onError: (error) => Notification("error", "Error", getMutationMessage(error, 'Unable to delete link')),
            onSettled: () => setIsSavingLink(false),
        });
    };

    const onLinkInputUrlBlur = (e: FocusEvent<HTMLInputElement>, index: number) => {

        if (e.target.value === '')
            return;

        // For invalid input show error hint & change class on input
        const hintElem = document.querySelector(`[data-link-id="${index}"]`) as HTMLElement;
        if (hintElem) {
            if (!isValidUrl(`https://${e.target.value}`)) {
                e.target.className = "form-control is-invalid";
                hintElem.style.display = "inline";
            } else {
                e.target.className = "form-control";
                hintElem.style.display = "none";
            }
        }
    };

    const onLinkInputChange = (e, index) => {
        let { name, value } = e.target;

        const newList = links.slice();

        if (name === 'editingUrl' && value) {
            // Keep the url's scheme
            const url = newList[index].url;
            value = url ? `${getUrlScheme(url)}${value}` : `https://${value}`
        }

        newList[index][name] = value;
        setLinks(newList);
    };

    const onCancelLink = (link: LocationTaskLink) => {
        const newLinks = links.slice();

        if (link.id === 0) {
            // Remove the last link from the list, which is the one that was being added
            newLinks.pop();
        } else if (isLinkInEdit(link.id)) {
            // Clear the editing values
            link.editingUrl = "";
            link.editingName = "";
            const index = newLinks.findIndex(x => x.ordinal === link.ordinal);
            newLinks.splice(index, 1, link);

            // Clear the link from the edit list
            const updatedEditIds = editLinkIds.filter(x => x !== link.id);
            setEditLinkIds(updatedEditIds);
        }

        setLinks(newLinks);
    }

    const onSaveLink = async (link: LocationTaskLink, index: number) => {
        let result = false;

        // Validate the temporary in-progress field values to see if they're valid
        const isLinkValid =
            link.editingUrl &&
            link.editingUrl.trim().length > 0 &&
            link.editingName &&
            link.editingName.trim().length > 0 &&
            isValidUrl(link.editingUrl);

        if (isLinkValid) {
            // Set the temporary in-progress field values to the actual field values
            if (link.editingUrl) {
                link.url = link.editingUrl;
            }
            if (link.editingName) {
                link.name = link.editingName;
            }

            setIsSavingLink(true);

            if (link.id === 0 || isLinkInEdit(link.id)) {
                return await new Promise<boolean>((resolve) => {
                    const mutation = link.id === 0 ? createLinkMutation : updateLinkMutation;
                    mutation.mutate(link, {
                        onSuccess: (response) => {
                            const savedLink = getSavedLink(response);
                            updateSavedLinkInList(savedLink ?? link);
                            Notification('success', link.id === 0 ? 'Link Added Successfully' : 'Link Saved Successfully', '');
                            resolve(true);
                        },
                        onError: (error) => {
                            Notification('error', 'Error', getMutationMessage(error, 'Unable to save link'));
                            resolve(false);
                        },
                        onSettled: () => setIsSavingLink(false),
                    });
                });
            }
        } else {
            const hintElem = document.querySelector(`[data-link-id="${index}"]`) as HTMLElement;
            if (hintElem) {
                hintElem.style.display = "inline";
            }
        }

        return result;
    };

    const updateSavedLinkInList = (link: LocationTaskLink) => {
        const newLinks = links.slice();
        const index = newLinks.findIndex((x) => x.ordinal === link.ordinal);
        newLinks.splice(index, 1, link);
        setLinks(newLinks);

        // Clear the link from the edit list
        const updatedEditIds = editLinkIds.filter(x => x !== link.id);
        setEditLinkIds(updatedEditIds);
    };

    const showAddEditLinks = !(links.filter(x => isLinkInEdit(x.id)).length) && !(files.filter(x => isFileInEdit(x.id)).length);

    return (
        <div className='links-and-files mb-3'>

            <div className='row'>
                <div className='col-12'>
                    <div className="links-and-files-panel card">

                        <div className='card-header'>
                            <h3 className='card-title'>
                                <i className='fa-thin fa-paperclip me-2'></i>
                                Links & Files
                                {/* {links || files ? (
                                    <span className='badge bg-secondary ms-2'>
                                        {(links?.length || 0) + (files?.length || 0)}
                                    </span>
                                ) : null} */}
                            </h3>
                            <div style={{ height: '21px', display: 'flex', gap: '12px' }}>

                                <div className={`link-with-icon ${!showAddEditLinks ? 'not-allowed' : ''}`}
                                    onClick={showAddEditLinks ? onAddLink : undefined}>
                                    <i className={'fa-thin fa-circle-plus'}></i>
                                    <button type='button' title="Add Link" className="btn btn-link" disabled={!showAddEditLinks}>
                                        Add Link
                                    </button>
                                </div>
                                <div className={`link-with-icon ${!showAddEditLinks ? 'not-allowed' : ''}`}
                                    onClick={showAddEditLinks ? onAddFile : undefined}>
                                    <i className={'fa-thin fa-circle-plus'}></i>
                                    <button type='button' title="Add File" className="btn btn-link" disabled={!showAddEditLinks}>
                                        Add File
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={`panel-body ${linksAndFilesCount === 0 ? 'empty' : ''}`}>
                            <div className='links-list'>
                                {links.map((link, index) => {
                                    if (link.baselineTaskLinkId && link.customerFileId) {
                                        return (
                                            <div key={link.ordinal} className='link-list-item-no-actions'>
                                                <button type='button' className="btn btn-link" onClick={(e) => onDownloadLink(link, e)}>
                                                    {link.name}
                                                </button>
                                                <div className='list-item-icon' title='Cannot remove or edit baseline task links'>
                                                    <i className='fa-thin fa-pencil'></i>
                                                    <i className='fa-thin fa-trash text-danger'></i>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        if (!isLinkInEdit(link.id)) {
                                            return (
                                                <div key={link.ordinal} className={`link-list-item${link.baselineTaskLinkId || !showAddEditLinks ? '-no-actions' : ''}`}>
                                                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                                                    <div className='list-item-icon' title={`${link.baselineTaskLinkId ? 'Cannot remove or edit baseline task links' : ''}`}>
                                                        <i className='fa-thin fa-pencil' onClick={() => onEditLink(index)}></i>
                                                        <i className='fa-thin fa-trash text-danger' onClick={() => onRemoveLink(index)}></i>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div key={link.ordinal}>
                                                    <LoadingIndicator isLoading={isSavingLink}>
                                                        <div className='link-list-item-editing'>
                                                            <div className='link-url'>
                                                                <div className='input-group'>
                                                                    <span className='input-group-text'>{getUrlScheme(link.editingUrl)}</span>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        name='editingUrl'
                                                                        id={`linkUrl_${index}`}
                                                                        value={getUrlNoScheme(link.editingUrl)}
                                                                        onChange={e => onLinkInputChange(e, index)}
                                                                        onBlur={e => onLinkInputUrlBlur(e, index)}
                                                                    />
                                                                </div>
                                                                <span className="url-error-hint" data-link-id={index}>Please enter a valid link.</span>
                                                            </div>

                                                            <input
                                                                name='editingName'
                                                                id={`linkName_${index}`}
                                                                type='text'
                                                                placeholder='Name your link'
                                                                className='form-control'
                                                                value={link.editingName}
                                                                onChange={(e) => onLinkInputChange(e, index)}
                                                            />

                                                            <div className='edit-buttons'>
                                                                <button type='button' className='btn btn-link btn-sm' onClick={() => onCancelLink(link)}>Cancel</button>
                                                                <button type='button' className='btn btn-primary btn-sm' onClick={() => onSaveLink(link, index)}>Save</button>
                                                            </div>
                                                        </div>
                                                    </LoadingIndicator>
                                                </div>
                                            );
                                        }
                                    }
                                })}
                            </div>

                            <div className='files-list'>
                                {files.map((file) => {

                                    if (!isFileInEdit(file.id)) {
                                        return <FileCell
                                            key={file.fileName}
                                            file={file}
                                            setIsSavingFile={setIsSavingFile}
                                            showGallery={showGallery}
                                            editFile={onEditFile}
                                            deleteFile={onDeleteFile}
                                            handleSavedFile={handleSavedFile}
                                            isMobile={props.isMobile}
                                        />;
                                    } else {
                                        return (
                                            <div key={file.id} className="file-list-item-editing-wrapper">
                                                <LoadingIndicator isLoading={isSavingFile}>
                                                    <form noValidate className={`file-list-item-editing ${isFileFormValid ? '' : 'was-validated'}`}>

                                                        <InputText name="fileName" label="File Name" type="text" placeholder="File Name"
                                                            isFocused={true}
                                                            value={file.editingName}
                                                            onChange={(e) => updateFile(file, "editingName", e.target.value)}
                                                            isRequired={true}
                                                            errorMessage="File Name is required"
                                                            maxLength={100} />

                                                        <TextArea rows={5} maxLength={500} name="fileDescription" label="File Description" type="text" placeholder="File Description"
                                                            value={file.editingDescription}
                                                            onChange={(e) => updateFile(file, "editingDescription", e.target.value)} />
                                                        <div className="remaining-chars text-end mb-3">{file.editingDescription.length}/500</div>

                                                        {
                                                            file.id === 0 &&
                                                            <div className="file-chooser-wrapper">
                                                                <FileChooser name="file"
                                                                    onChange={(e) => onFileReplaced(file, e)}
                                                                    isRequired={true}
                                                                    errorMessage="A file is required"
                                                                />
                                                            </div>
                                                        }
                                                        {
                                                            file.id !== 0 && !file.showFileInput &&
                                                            <div className="edit-buttons mb-3">
                                                                <button type="button" className="btn btn-link btn-sm" onClick={() => updateFile(file, "showFileInput", true)}>Replace File</button>
                                                            </div>
                                                        }
                                                        {
                                                            file.id !== 0 && file.showFileInput &&
                                                            <div className="file-chooser-wrapper">
                                                                <FileChooser name="file"
                                                                    onChange={(e) => onFileReplaced(file, e)}
                                                                    isRequired={true}
                                                                    errorMessage="A file is required"
                                                                />
                                                            </div>
                                                        }

                                                        <div className='edit-buttons'>
                                                            <button type='button' className='btn btn-link btn-sm' onClick={() => onCancelFile(file)}>Cancel</button>
                                                            <button type='button' className='btn btn-primary btn-sm' onClick={() => onSaveFile(file)}>Save</button>
                                                        </div>

                                                    </form>
                                                </LoadingIndicator>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImageGalleryModal modalRef={galleryModalRef} files={files.filter(f => f.imageObjectURL)} initialIndex={activeImageIndex.index} />
        </div>
    );
};

export default LinksAndFiles;
